import { AUTH_URL } from '../../services/apiConfig';
import { fetchJsonWithAuth } from '../../services/authApi';

// =============================================================================
// Stored values that fall outside a filtered dropdown
// -----------------------------------------------------------------------------
// A lookup field's `lookupFilters` answer "who may I PICK?" — the jobs
// Assign-to picker lists only recruiters who report to the signed-in user. They
// were also, accidentally, answering "whose name may I SEE?": a value already on
// the record but outside that filtered set had no matching option, so antd fell
// back to printing the raw stored value. The edit form showed "149" and "1"
// where it should have shown two people's names.
//
// That is not a cosmetic problem. The user cannot tell who the job is assigned
// to, cannot verify it, and cannot even tell whether removing the chip is safe.
// And it is invisible to whoever configured the filter, because it only shows up
// for records assigned before the filter existed, or by somebody higher up the
// reporting chain.
//
// So: options stay filtered, and any value ALREADY STORED is resolved
// separately and added to the list. Nothing here names a module or a field.
// =============================================================================

/** The values a select currently holds, flattened to plain scalars. */
export function selectedValues(value) {
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((item) => (item && typeof item === 'object' ? (item.value ?? item.key ?? item.id) : item))
    .filter((item) => item !== undefined && item !== null && item !== '');
}

/**
 * missingOptionValues — stored values with no option to render them.
 *
 * Compared as STRINGS: a legacyUserId is the number 149 on the record and may
 * arrive as "149" from the option list. Comparing them raw would report every
 * value as missing and re-fetch on every render.
 */
export function missingOptionValues(value, options = []) {
  const known = new Set((options ?? []).map((opt) => String(opt?.value)));
  const missing = [];
  for (const v of selectedValues(value)) {
    const key = String(v);
    if (!known.has(key) && !missing.includes(key)) missing.push(key);
  }
  return missing;
}

/**
 * fetchLookupLabels — labels for specific stored values, unfiltered.
 *
 * Uses the SAME endpoint the options come from, in its `values` mode, so the
 * label a resolved chip shows is built by the same displayField/displayField2
 * the dropdown itself uses — a separately-built label would drift from the list
 * the moment an admin changed either.
 */
export async function fetchLookupLabels(field, values) {
  if (!field?.lookupCollection || !values?.length) return [];
  const params = new URLSearchParams({
    collection: field.lookupCollection,
    displayField: field.displayField ?? '',
    valueField: field.valueField ?? '_id',
    values: values.join(','),
  });
  if (field.displayField2) params.set('displayField2', field.displayField2);
  const json = await fetchJsonWithAuth(AUTH_URL, `/admin/lookup-dropdown-values?${params}`);
  const rows = json?.data ?? json ?? [];
  return Array.isArray(rows) ? rows : [];
}

/**
 * mergeResolvedOptions — the filtered list plus the resolved stragglers.
 *
 * Resolved entries are marked `resolvedOnly` so a caller can tell them apart.
 * They are NOT disabled: the value is on the record, and the user must be able
 * to remove it. What they cannot do is add it back once removed — which is
 * exactly what the filter is there to prevent, and is now the only thing it
 * prevents.
 */
export function mergeResolvedOptions(options = [], resolved = []) {
  if (!resolved.length) return options;
  const known = new Set((options ?? []).map((opt) => String(opt?.value)));
  const extra = resolved
    .filter((row) => !known.has(String(row?.value)))
    .map((row) => ({ ...row, resolvedOnly: true }));
  return extra.length ? [...options, ...extra] : options;
}

/**
 * labelForMissingValue — the last resort when even the lookup finds nothing.
 *
 * A deleted user, or an id that never existed. Showing the bare number implies
 * it is a name; this says plainly that it could not be resolved while keeping
 * the id visible, because the id is the only thing left to investigate with.
 */
export function labelForMissingValue(value) {
  return `Unknown (${value})`;
}
